/* eslint-disable react-refresh/only-export-components */
import { useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { sanitizeMoneyInput } from "../../../../../utils/money";
import {
  BillTotalsBreakdown,
  type BillClientDiscount,
} from "./BillTotalsBreakdown";
import { LEAVE_FIELD_ON_ARROWS } from "../listKeyboard";

export const BANKNOTES = [5, 10, 20, 50, 100];

/** До 2 знака след разделителя; запетая = точка; само цифри и една точка —
 *  общото правило от `utils/money` (и за „Обща сума" на бакшиша). */
export const sanitizeReceivedInput = sanitizeMoneyInput;

interface CashChangePanelProps {
  /** Общо за плащане (низ от bill.total_amount). */
  totalAmount: string;
  /** Полученото като НИЗ (контролирано отвън, за да се чисти при затваряне). */
  received: string;
  onReceivedChange: (value: string) => void;
  disabled?: boolean;
  /** Клиентският процент — редовете над „Общо" (виж BillTotalsBreakdown). */
  discount?: BillClientDiscount | null;
}

/**
 * Помощник за ресто при метод „В брой" — чисто ВИЗУАЛЕН, само FE удобство:
 * не участва в payload-а към closeBill и не променя нищо по закриването.
 * Елементите лежат направо върху тонираната лява колона на модала (бели
 * карти върху топлия фон). Банкнотите се ДОБАВЯТ към полученото, „Точно"
 * го попълва до сметката. „Остава за доплащане" се обръща на зеления
 * панел „Ресто за връщане", щом полученото покрие сметката.
 */
export const CashChangePanel = ({
  totalAmount,
  received,
  onReceivedChange,
  disabled,
  discount = null,
}: CashChangePanelProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const total = parseFloat(totalAmount) || 0;
  const receivedNumber = parseFloat(received) || 0;
  const difference = receivedNumber - total;
  const isChange = received !== "" && difference >= 0;

  const addBanknote = (banknote: number) => {
    if (disabled) return;
    onReceivedChange((receivedNumber + banknote).toFixed(2));
    inputRef.current?.focus();
  };

  const fillExactAmount = () => {
    if (disabled) return;
    onReceivedChange(total.toFixed(2));
    inputRef.current?.focus();
  };

  // При излизане от полето сумата се нормализира до 2 знака:
  // „2" → 2.00, „.5" → 0.50, „2,6" → 2.60
  const normalizeOnBlur = () => {
    if (received === "") return;
    onReceivedChange((parseFloat(received) || 0).toFixed(2));
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Банкноти — добавят се към полученото */}
      <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
        {t("closeTable.banknotes")}
      </div>
      <div className="mb-4 grid grid-cols-3 gap-2">
        {BANKNOTES.map((banknote) => (
          <button
            key={banknote}
            type="button"
            disabled={disabled}
            onClick={() => addBanknote(banknote)}
            className="h-12 cursor-pointer rounded-xl border border-pos-ctl bg-pos-card font-pos-mono text-base font-semibold text-pos-ink2 transition-colors hover:border-pos-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            +{banknote}
          </button>
        ))}
        {/* „Точно" — попълва полученото 1:1 със сметката (ресто 0.00) */}
        <button
          type="button"
          disabled={disabled}
          onClick={fillExactAmount}
          className="h-12 cursor-pointer rounded-xl border border-pos-accent/60 bg-pos-card text-sm font-bold text-pos-accent-ink transition-colors hover:bg-pos-marked disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("closeTable.exactAmount")}
        </button>
      </div>

      {/* Получено — клик върху целия ред фокусира полето */}
      <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
        {t("closeTable.received")}
      </div>
      <div
        onClick={() => inputRef.current?.focus()}
        className="flex h-14 cursor-text items-center gap-2 rounded-xl border border-pos-ctl bg-pos-card px-4 focus-within:border-pos-accent"
      >
        <input
          ref={inputRef}
          {...LEAVE_FIELD_ON_ARROWS}
          value={received}
          disabled={disabled}
          inputMode="decimal"
          placeholder="0.00"
          onChange={(e) =>
            onReceivedChange(sanitizeReceivedInput(e.target.value))
          }
          onFocus={(e) => e.target.select()}
          onBlur={normalizeOnBlur}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-right font-pos-mono text-xl font-bold text-pos-ink outline-none placeholder:text-pos-faint"
        />
        <span className="shrink-0 font-pos-mono text-[13px] text-pos-muted">
          EUR
        </span>
        <button
          type="button"
          title={t("common.cancel")}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onReceivedChange("");
            inputRef.current?.focus();
          }}
          className="grid h-7 w-7 shrink-0 cursor-pointer place-items-center rounded-full border border-pos-ctl bg-pos-card text-[11px] text-pos-dim disabled:cursor-not-allowed"
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Ресто за връщане (зелен панел) / Остава за доплащане */}
      {isChange ? (
        <div className="mt-4 flex items-baseline justify-between gap-2 rounded-xl bg-pos-served-bg px-3.5 py-3">
          <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-pos-served">
            {t("closeTable.change")}
          </span>
          <span className="font-pos-mono text-[21px] font-bold tracking-[-0.5px] text-pos-served">
            {difference.toFixed(2)} <span className="text-sm">EUR</span>
          </span>
        </div>
      ) : (
        <div className="mt-4 flex items-baseline justify-between gap-2 px-0.5">
          <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-pos-sec-head">
            {t("closeTable.remaining")}
          </span>
          <span className="font-pos-mono text-[21px] font-bold tracking-[-0.5px] text-pos-accent">
            {(total - receivedNumber).toFixed(2)}{" "}
            <span className="text-sm">EUR</span>
          </span>
        </div>
      )}

      {/* Общо — долу, под разделител; с клиент и редовете откъде идва сумата */}
      <BillTotalsBreakdown total={total} discount={discount} />
    </div>
  );
};
